import React from 'react';
import { Button, Space } from 'antd';
import { buttonStyles } from '../../../theme/theme';
import {
    SearchOutlined,
    DownloadOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    UserOutlined,
    SettingOutlined,
    ReloadOutlined,
    CheckOutlined
} from '@ant-design/icons';
import {
    PrimaryButton,
    OutlineButton,
    SecondaryButton,
    AccentButton,
    SuccessButton,
    InfoButton,
    WarningButton,
    ErrorButton,
    LinkButton,
    GhostButton,
    IconButton,
    BlockButton
} from '../Buttons/Buttons';
import ButtonState from '../Buttons/ButtonState';

// Costanti dei colori
const DARK_BG = '#1f1f1f';
const WHITE = 'white';

/**
 * Component that shows all button types available in the project.
 * Can be used as a reference.
 */
const ButtonsComponent = () => {
    return (
        <div>
            {/* Section 1: Basic styles */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)' }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Standard Buttons</h3>
                <Space wrap size={16}>
                    <PrimaryButton>Primary</PrimaryButton>
                    <OutlineButton>Outline</OutlineButton>
                    <SecondaryButton>Secondary</SecondaryButton>
                    <AccentButton>Accent</AccentButton>
                    <Button disabled>Disabled</Button>
                </Space>
            </div>

            {/* Section 2: Status buttons */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)' }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Status Buttons</h3>
                <Space wrap size={16}>
                    <SuccessButton>Success</SuccessButton>
                    <InfoButton>Information</InfoButton>
                    <WarningButton>Warning</WarningButton>
                    <ErrorButton>Error</ErrorButton>
                </Space>
            </div>

            {/* Section 2.1: Validation Status */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)' }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Validation Status</h3>
                <Space wrap size={16}>
                    <ButtonState state="Incomplete" text="Incomplete" />
                    <ButtonState state="Modelist Rev." text="Modelist Rev." />
                    <ButtonState state="Spaarkly Rev." text="Spaarkly Rev." />
                    <ButtonState state="Client Rev." text="Client Rev." />
                    <ButtonState state="In Publication" text="In Publication" />
                </Space>
                <div style={{ marginTop: '16px' }}>
                    <Space wrap size={16}>
                        <ButtonState state="Incomplete" text="1" isCircle={true} />
                        <ButtonState state="Modelist Rev." text="2" isCircle={true} />
                        <ButtonState state="Spaarkly Rev." text="3" isCircle={true} />
                        <ButtonState state="Client Rev." text="4" isCircle={true} />
                        <ButtonState state="In Publication" text="5" isCircle={true} />
                    </Space>
                </div>
            </div>

            {/* Section 3: Button sizes */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)' }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Sizes</h3>
                <Space wrap size={16}>
                    <PrimaryButton size="small">Small</PrimaryButton>
                    <PrimaryButton>Default</PrimaryButton>
                    <PrimaryButton size="large">Large</PrimaryButton>
                </Space>
            </div>

            {/* Section 4: Buttons with icons */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)' }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Buttons with Icons</h3>
                <Space wrap size={16}>
                    <PrimaryButton icon={<SearchOutlined />}>Search</PrimaryButton>
                    <OutlineButton icon={<DownloadOutlined />}>Download</OutlineButton>
                    <SuccessButton icon={<PlusOutlined />}>Add</SuccessButton>
                    <ErrorButton icon={<DeleteOutlined />}>Delete</ErrorButton>
                    <InfoButton icon={<EditOutlined />}>Edit</InfoButton>
                </Space>
            </div>

            {/* Section 5: Icon only */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)' }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Icon Only Buttons</h3>
                <Space wrap size={16}>
                    <IconButton icon={<UserOutlined />} tooltip="User" />
                    <IconButton icon={<SettingOutlined />} tooltip="Settings" type="primary" />
                    <IconButton icon={<ReloadOutlined />} tooltip="Reload" style={buttonStyles.info} />
                    <IconButton icon={<CheckOutlined />} tooltip="Confirm" style={buttonStyles.success} />
                    <IconButton icon={<DeleteOutlined />} tooltip="Delete" style={buttonStyles.error} />
                </Space>
            </div>

            {/* Section 6: Button shapes */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)' }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Button Shapes</h3>
                <Space wrap size={16}>
                    <PrimaryButton>Default</PrimaryButton>
                    <IconButton icon={<SearchOutlined />} type="primary" />
                    <PrimaryButton shape="round">Round</PrimaryButton>
                    <PrimaryButton icon={<SearchOutlined />} shape="round">Rounded with Icon</PrimaryButton>
                </Space>
            </div>

            {/* Section 7: Block width buttons */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)' }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Full Width Buttons</h3>
                <div style={{ width: '100%', maxWidth: 'clamp(18.75rem, 60vw, 31.25rem)' }}>
                    <BlockButton type="primary" style={{ marginBottom: 'clamp(0.3125rem, 1.5vw, 0.625rem)' }}>Full Width Button</BlockButton>
                    <BlockButton className="button-outline" type={null} style={{ ...buttonStyles.outline, marginBottom: 'clamp(0.3125rem, 1.5vw, 0.625rem)' }}>Full Width Outline Button</BlockButton>
                    <BlockButton className="button-success button-enhance" type={null} style={buttonStyles.success}>Full Width Success Button</BlockButton>
                </div>
            </div>

            {/* Section 8: Ghost buttons */}
            <div style={{ marginBottom: 'clamp(1rem, 4vw, 1.875rem)', padding: 'clamp(0.75rem, 3vw, 1.25rem)', background: DARK_BG }}>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)', color: WHITE }}>Ghost Buttons (on dark background)</h3>
                <Space wrap size={16}>
                    <GhostButton type="primary">Primary Ghost</GhostButton>
                    <GhostButton type="default">Default Ghost</GhostButton>
                    <GhostButton type="dashed">Dashed Ghost</GhostButton>
                </Space>
            </div>

            {/* Section 9: Link buttons */}
            <div>
                <h3 style={{ marginBottom: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Link Buttons</h3>
                <Space wrap size={16}>
                    <LinkButton>Link Button</LinkButton>
                    <LinkButton danger>Link Danger</LinkButton>
                </Space>
            </div>
        </div>
    );
};

export default ButtonsComponent; 